import { Purchases } from '@revenuecat/purchases-js'
import React, { useEffect, useState } from 'react'

import { paymentSessionContext } from './lib'

import { Loading } from '@/components/loading'
import { getRevenueCatApiKey } from '@/config/RCPurchaseController'
import { resolveRcAppUserId } from '@/services/rcIdentity'
import { useOptionalUser } from '@/pages/user/selectors'
import { useSubscriptions } from '@/services'
import { useAppDispatch } from '@/hooks/useRedux'
import { updateSubscriptions } from '@/pages/user/userSlice'
import { User } from '@/types/User'
import { useErrorBoundary } from 'react-error-boundary'
import { InternalMisconfigurationError } from './ErrorComponents'

/**
 * RevenueCat provider for the main app flows (subscription page, subscribe modal, etc.).
 * Uses Redux for user state and subscription management.
 */
export function ProviderRevenueCat({ children }: { children: React.ReactNode }) {
  const user = useOptionalUser()
  const [revenueCat, setRevenueCat] = useState<Purchases | null>(null)
  const dispatch = useAppDispatch()
  const { showBoundary } = useErrorBoundary()

  const { mutate: subscriptions } = useSubscriptions()

  useEffect(() => {
    subscriptions(undefined, {
      onSuccess: (res) => {
        dispatch(updateSubscriptions(res.subscriptions))
      },
    })
  }, [subscriptions, dispatch])

  useEffect(() => {
    if (user) {
      Promise.all([getRevenueCatApiKey(), resolveRcAppUserId(user.id)])
        .then(([apiKey, appUserId]) => {
          const rc = Purchases.configure(apiKey, appUserId)
          setRevenueCat(rc)
        })
        .catch((error) => {
          console.error('Failed to configure RevenueCat:', error)
          showBoundary(new InternalMisconfigurationError('Failed to initialize payment provider'))
        })
    }
  }, [user?.id, showBoundary])

  if (!revenueCat) {
    return <Loading />
  }

  return <PaymentSession value={{ revenueCat, token: '' }}>{children}</PaymentSession>
}

/**
 * RevenueCat provider for external/isolated flows (premium login, partner integrations).
 * Takes user and token as explicit props — zero Redux dependency.
 */
export function ExternalRevenueCatProvider({
  children,
  token,
  user,
}: {
  children: React.ReactNode
  token: string
  user: User
}) {
  const [revenueCat, setRevenueCat] = useState<Purchases | null>(null)
  const { showBoundary } = useErrorBoundary()

  useEffect(() => {
    Promise.all([getRevenueCatApiKey(), resolveRcAppUserId(user.id)])
      .then(([apiKey, appUserId]) => {
        const rc = Purchases.configure(apiKey, appUserId)
        setRevenueCat(rc)
      })
      .catch((error) => {
        console.error('Failed to configure RevenueCat:', error)
        showBoundary(new InternalMisconfigurationError('Failed to initialize payment provider'))
      })
  }, [user.id, showBoundary])

  if (!revenueCat) {
    return <Loading />
  }

  return <PaymentSession value={{ revenueCat, token }}>{children}</PaymentSession>
}

const PaymentSession = paymentSessionContext.Provider
